// Bảng tra hộp hướng dẫn NHIỀU THẺ — một nguồn cho mọi trang chia bài theo thẻ.
//
// Vì sao có file này thay vì chép thêm hộp thứ ba, thứ tư, thứ năm: hai hộp
// riêng đầu tiên (Cơ chế lương, Thanh toán) đã kèm sẵn lời dặn "đến ngoại lệ
// thứ ba thì dừng chép tay, đổi sang bảng tra". Ba trang ca tới cùng một lúc
// chính là lúc đó. Năm bản chép tay gần giống nhau sẽ lệch dần, và lệch ở hộp
// nào thì chỉ người mở đúng hộp ấy mới thấy.
//
// Mỗi trang chỉ khai DỮ LIỆU của riêng nó — thứ tự thẻ, ai mở được thẻ nào,
// màu nhãn trạng thái, dải vòng đời — còn cách bày do `MultiTabGuideDialog`
// lo. Sửa cách bày một chỗ là cả bốn trang theo.

import type { Audience } from "./audience";
import type { StatusTone } from "./GuideSections";

import { PAYMENT_GUIDE_ID, PAYMENT_GUIDE } from "./payment-guide-tabs";
import { BRANCH_SHIFT_GUIDE_ID, BRANCH_SHIFT_GUIDE } from "./branch-shift-guide-tabs";
import { LETAN_SHIFT_GUIDE_ID, LETAN_SHIFT_GUIDE } from "./letan-shift-guide-tabs";
import { EKIP_SHIFT_GUIDE_ID, EKIP_SHIFT_GUIDE } from "./ekip-shift-guide-tabs";
import { STAFF_GUIDE_ID, STAFF_GUIDE } from "./staff-guide-tabs";
import { COMPLAINTS_GUIDE_ID, COMPLAINTS_GUIDE } from "./complaints-guide-tabs";

/**
 * Một chặng trong vòng đời mà các thẻ của trang cùng nhìn vào.
 *
 * Có mặt vì đứng trong MỘT thẻ thì không thấy được mạch: người đọc học thuộc
 * "thẻ này làm gì" mà vẫn không hiểu vì sao một bản ghi biến khỏi thẻ mình rồi
 * hiện ra ở thẻ người khác.
 */
export type GuideFlowStage = {
  /** Tên chặng, ngắn — đây là chữ to nhất trong ô. */
  ten: string;
  tone: StatusTone;
  /** Chuyện gì xảy ra ở chặng này. Một câu, nói việc chứ không nói cơ chế. */
  ke: string;
  /** Thẻ nào của màn nhìn thấy chặng này — chỗ nối dải với hàng thẻ. */
  the: readonly string[];
  /** Chặng rẽ nhánh, không phải bước tiếp theo của mạch chính. */
  nhanh?: boolean;
};

export type GuideFlow = {
  /** Tiêu đề dải, đặt theo thứ đang chạy trên mạch: "Một phiếu thu đi qua đâu". */
  heading: string;
  /** Một câu nói dải này để làm gì. */
  intro: string;
  stages: readonly GuideFlowStage[];
};

/**
 * Phần khai của một trang có hộp nhiều thẻ.
 *
 * Kiểu ở đây cố ý KHÔNG ràng buộc khoá thẻ (`string` chứ không phải union của
 * từng trang): bảng tra gom bốn trang có bốn bộ khoá khác nhau. Ràng buộc lúc
 * build vẫn còn nguyên, nhưng nằm trong file khai của từng trang — nơi
 * `satisfies` đối chiếu với đúng từ điển nhãn của màn ấy.
 */
export type MultiTabGuideConfig = {
  /** Thứ tự thẻ trong hộp = thứ tự thẻ trên màn. */
  tabOrder: readonly string[];
  /**
   * Nhóm nghề nào MỞ ĐƯỢC thẻ nào trên màn thật.
   *
   * Dữ liệu kỹ thuật để hộp bày đúng số thẻ, KHÔNG phải nội dung hiển thị —
   * "ai mở được gì" chính là thứ ranh giới thông tin cấm kể.
   *
   * Khai TAY chứ không suy từ "thẻ này có bài của họ": hai thứ đó lệch nhau và
   * lệch theo hướng nguy hiểm. Người không có bài ở thẻ nào cả mà rơi vào
   * nhánh "thôi hiện hết" là đọc trọn bộ thẻ — đo 12/09/2026 trên bàn xem thử,
   * đúng cái rò mà cả tính năng này sinh ra để chặn.
   */
  theAiMoDuoc: Record<string, Audience[]>;
  /**
   * Sắc thái của từng nhãn trạng thái, khớp màu badge trên màn thật.
   *
   * Tra theo NHÃN chứ không theo thẻ: cùng một nhãn xuất hiện ở nhiều thẻ và
   * phải mang cùng một màu ở mọi nơi — đó chính là thứ giúp người đọc nhận ra
   * nó. Hệ quả: một nhãn dùng cho hai nghĩa trên cùng trang chỉ có MỘT màu.
   */
  tone?: Record<string, StatusTone>;
  /** Dải vòng đời. Trang không có mạch rõ ràng thì bỏ trống, hộp tự ẩn dải. */
  flow?: GuideFlow;
};

/** Trang nào dùng hộp nhiều thẻ. Khoá là id trang trong `nav-items.ts`. */
export const MULTI_TAB_GUIDES: Record<string, MultiTabGuideConfig> = {
  [PAYMENT_GUIDE_ID]: PAYMENT_GUIDE,
  [BRANCH_SHIFT_GUIDE_ID]: BRANCH_SHIFT_GUIDE,
  [LETAN_SHIFT_GUIDE_ID]: LETAN_SHIFT_GUIDE,
  [EKIP_SHIFT_GUIDE_ID]: EKIP_SHIFT_GUIDE,
  [STAFF_GUIDE_ID]: STAFF_GUIDE,
  [COMPLAINTS_GUIDE_ID]: COMPLAINTS_GUIDE,
};
